/**
 * SmartViewToggle — the follow-edits ("smart view") pill, top-right of the board
 * chrome. Labelled rather than icon-only so it reads as a real control; the
 * tooltip explains the behaviour and the accent state shows on/off at a glance.
 */
import { Crosshair } from "lucide-react";
import { useTranslation } from "../i18n";
import { useFlashOnChange } from "./useFlashOnChange";
import { HudChip } from "../components/HudChip";

export function SmartViewToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  const { t } = useTranslation("boardControls");
  // Flash a ring on every toggle so the ⌃⌥F hotkey (fired with the editor
  // focused, and especially when toggling OFF — no camera motion to notice)
  // still gives a visible "registered" cue in the board chrome.
  const flash = useFlashOnChange(on);
  return (
    <HudChip
      onClick={onToggle}
      active={on}
      accent="arch"
      role="switch"
      aria-checked={on}
      aria-label={t("smartViewAriaLabel")}
      className={flash ? "ring-2 ring-arch-400/80" : ""}
      title={on ? t("smartViewTitleOn") : t("smartViewTitleOff")}
    >
      <CrosshairIcon />
      {/* Label collapses to icon-only on phone-width screens. */}
      <span className="hidden sm:inline">{t("smartViewLabel")}</span>
    </HudChip>
  );
}

function CrosshairIcon() {
  return <Crosshair width={16} height={16} strokeWidth={2} aria-hidden="true" />;
}
